import { useCallback, useEffect } from 'react';

import { bindHeaderControls } from '../lib/bindHeaderControls';
import { getLocalDateString } from '../lib/time';
import { getAdjacentTimelineZoomLevel } from '../lib/zoom';

const DEFAULT_ZOOM = 1;
const ZOOM_RESET_EPSILON = 0.001;

function setText(el, text) {
  if (!el) return;
  el.textContent = String(text ?? '');
}

function getHeaderZoomControl() {
  return document.getElementById('zoomLevel')
    || document.getElementById('zoomLevelHeader')
    || document.getElementById('zoomLabel');
}

function shiftISODate(dateStr, dayDelta) {
  const match = String(dateStr ?? '').match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return getLocalDateString();

  const [, year, month, day] = match;
  const date = new Date(Number(year), Number(month) - 1, Number(day));
  date.setDate(date.getDate() + dayDelta);

  const nextYear = date.getFullYear();
  const nextMonth = String(date.getMonth() + 1).padStart(2, '0');
  const nextDay = String(date.getDate()).padStart(2, '0');
  return `${nextYear}-${nextMonth}-${nextDay}`;
}

export default function useTimelineHeaderBridge({
  zoom,
  liveFollowActive,
  selectedDate,
  isToday,
  zoomWithAnchor,
  scrollToNow,
  setSelectedDate,
  setLiveFollowActive,
}) {
  const setHeaderZoomLabel = useCallback((z) => {
    const labelEl = getHeaderZoomControl();
    setText(labelEl, `${Math.round(z * 100)}%`);
  }, []);

  const setHeaderZoomControlState = useCallback((z) => {
    const btn = getHeaderZoomControl();
    if (!btn) return;

    const canReset = Math.abs(Number(z) - DEFAULT_ZOOM) > ZOOM_RESET_EPSILON;
    btn.classList.toggle('disabled', !canReset);
    btn.classList.toggle('timeline-zoom-reset-control', canReset);
    btn.setAttribute('aria-disabled', canReset ? 'false' : 'true');
    btn.setAttribute(
      'aria-label',
      canReset ? 'Zoom auf 100 Prozent zuruecksetzen' : 'Zoom 100 Prozent'
    );
    btn.title = canReset ? 'Zoom auf 100 % zuruecksetzen' : 'Zoom 100 %';
    if (canReset) {
      btn.removeAttribute('tabindex');
    } else {
      btn.setAttribute('tabindex', '-1');
    }
  }, []);

  const setHeaderNowLabel = useCallback((dateStr) => {
    const labelEl = document.getElementById('nowButtonLabel');
    if (!labelEl) return;

    const today = getLocalDateString();
    if (!dateStr || dateStr === today) {
      setText(labelEl, 'Live');
      return;
    }

    setText(labelEl, dateStr);
  }, []);

  const setHeaderDatePickerValue = useCallback((dateStr) => {
    const input = document.getElementById('timelineDatePicker');
    if (!input) return;
    input.value = String(dateStr ?? '');
  }, []);

  const setHeaderNowButtonState = useCallback((active) => {
    const btn = document.getElementById('nowButton');
    if (!btn) return;

    const enabled = isToday;

    btn.classList.add('btn-outline-light');
    btn.classList.remove('btn-light');
    btn.classList.toggle('is-live-following', Boolean(active) && enabled);
    btn.setAttribute('aria-pressed', active && enabled ? 'true' : 'false');

    btn.disabled = !enabled;
    btn.classList.toggle('disabled', !enabled);
    btn.setAttribute('aria-disabled', !enabled ? 'true' : 'false');
    btn.title = enabled
      ? 'Live-Follow umschalten'
      : 'Live ist nur für den heutigen Tag verfügbar';
  }, [isToday]);

  useEffect(() => {
    setHeaderZoomLabel(zoom);
    setHeaderZoomControlState(zoom);
    setHeaderNowButtonState(liveFollowActive);
    setHeaderDatePickerValue(selectedDate);
    setHeaderNowLabel(selectedDate);
  }, [
    liveFollowActive,
    selectedDate,
    setHeaderDatePickerValue,
    setHeaderNowButtonState,
    setHeaderNowLabel,
    setHeaderZoomControlState,
    setHeaderZoomLabel,
    zoom,
  ]);

  useEffect(() => {
    document.dispatchEvent(new CustomEvent('timeline:date-changed', {
      detail: {
        date: selectedDate,
        is_today: isToday,
      }
    }));
  }, [isToday, selectedDate]);

  useEffect(() => {
    const unbind = bindHeaderControls({
      onZoomIn: () => zoomWithAnchor(getAdjacentTimelineZoomLevel(zoom, 1)),
      onZoomOut: () => zoomWithAnchor(getAdjacentTimelineZoomLevel(zoom, -1)),
      onZoomReset: () => {
        if (Math.abs(Number(zoom) - DEFAULT_ZOOM) <= ZOOM_RESET_EPSILON) return;
        zoomWithAnchor(DEFAULT_ZOOM);
      },

      onNowToggle: () => {
        if (!isToday) return;

        setLiveFollowActive((prev) => {
          const next = !prev;
          if (next) {
            requestAnimationFrame(() => {
              scrollToNow(true);
            });
          }
          return next;
        });
      },

      onDateInput: (dateStr) => {
        if (!dateStr) return;
        setSelectedDate(String(dateStr));
        setLiveFollowActive(false);
      },

      onTodayDate: () => {
        const today = getLocalDateString();
        setSelectedDate(today);
        setLiveFollowActive(false);
      },

      onPreviousDate: (event) => {
        event?.preventDefault?.();
        event?.stopPropagation?.();
        setSelectedDate((current) => shiftISODate(current, -1));
        setLiveFollowActive(false);
      },

      onNextDate: (event) => {
        event?.preventDefault?.();
        event?.stopPropagation?.();
        setSelectedDate((current) => shiftISODate(current, 1));
        setLiveFollowActive(false);
      },
    });

    return () => {
      if (typeof unbind === 'function') unbind();
    };
  }, [
    isToday,
    scrollToNow,
    setLiveFollowActive,
    setSelectedDate,
    zoom,
    zoomWithAnchor,
  ]);

  return {
    setHeaderZoomLabel,
    setHeaderZoomControlState,
    setHeaderNowLabel,
    setHeaderDatePickerValue,
    setHeaderNowButtonState,
  };
}
